import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import "./Landing.css";

/**
 * Terminal-EX ana giriş — login arkaplanı ile birebir aynı estetik.
 * - Marka logosu: paylaşılan <BrandLogo loop /> ile typing animasyonu
 * - Fare takipli grid + spot + drift parçacık canvas
 * - "Terminal'e Geç" → /login
 */
export default function Landing() {
  const navigate = useNavigate();
  const bgRef = useRef(null);
  const cardRef = useRef(null);
  const canvasRef = useRef(null);

  // Fontları head'e bir kez ekle (Outfit + JetBrains Mono)
  useEffect(() => {
    const id = "tx-fonts";
    if (document.getElementById(id)) return;
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    document.head.appendChild(pre1);
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    document.head.appendChild(pre2);
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
  }, []);

  // Fare takipli arka plan: grid hot mask + spot + drift particles
  useEffect(() => {
    const bg = bgRef.current;
    const card = cardRef.current;
    const canvas = canvasRef.current;
    if (!bg || !canvas) return;

    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let cx = tx;
    let cy = ty;
    let overCard = false;
    let raf1 = 0;
    let raf2 = 0;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    const onEnter = () => {
      overCard = true;
      bg.classList.add("tx-over");
    };
    const onLeave = () => {
      overCard = false;
      bg.classList.remove("tx-over");
    };
    if (card) {
      card.addEventListener("pointerenter", onEnter);
      card.addEventListener("pointerleave", onLeave);
    }

    const loop = () => {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      bg.style.setProperty("--mx", cx + "px");
      bg.style.setProperty("--my", cy + "px");
      raf1 = requestAnimationFrame(loop);
    };
    raf1 = requestAnimationFrame(loop);

    const ctx = canvas.getContext("2d");
    let W = 0;
    let H = 0;
    let parts = [];
    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
      const count = Math.min(70, Math.floor((W * H) / 26000));
      parts = Array.from({ length: count }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.6 + 0.4,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        const dx = cx - p.x;
        const dy = cy - p.y;
        const d2 = dx * dx + dy * dy;
        if (!overCard && d2 < 240 * 240) {
          const f = (1 - Math.sqrt(d2) / 240) * 0.06;
          p.vx += dx * 0.00006 * f * 100;
          p.vy += dy * 0.00006 * f * 100;
        }
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.96;
        p.vy *= 0.96;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;
        const dist = Math.sqrt(d2);
        const near = !overCard && dist < 240;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = near
          ? `rgba(255,93,21,${0.15 + (1 - dist / 240) * 0.5})`
          : "rgba(255,255,255,0.12)";
        ctx.fill();
      }
      raf2 = requestAnimationFrame(draw);
    };
    if (ctx) raf2 = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("resize", resize);
      if (card) {
        card.removeEventListener("pointerenter", onEnter);
        card.removeEventListener("pointerleave", onLeave);
      }
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  return (
    <div className="tx-landing" data-testid="landing-root">
      <div className="tx-bgfx" ref={bgRef}>
        <div className="tx-grid" />
        <div className="tx-grid-hot" />
        <canvas ref={canvasRef} />
        <div className="tx-amb1" />
        <div className="tx-amb2" />
        <div className="tx-spot" />
        <div className="tx-vig" />
      </div>

      <main className="tx-card" ref={cardRef}>
        <span className="tx-c3" />
        <span className="tx-c4" />

        <div className="tx-brand">
          <div className="tx-mark">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#190401"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8.5 16.5V7.5l-3 3M8.5 7.5l3 3" />
              <path d="M15.5 7.5v9l3-3M15.5 16.5l-3-3" />
            </svg>
          </div>
          <BrandLogo size="lg" loop={true} align="center" eyebrow="Darkex Hedge" />
        </div>

        <p className="tx-tag">
          Darkex'teki kaybını karşı borsada hedge'le, bonusu{" "}
          <b>saf kâra</b> çevir.
        </p>

        <button
          type="button"
          className="tx-btn-go"
          onClick={() => navigate("/login")}
          data-testid="landing-enter-btn"
        >
          <svg viewBox="0 0 24 24">
            <path
              d="M4 12h13M11 6l6 6-6 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M17 4h3v16h-3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Terminal'e Geç
        </button>

        <div className="tx-warn" data-testid="landing-security-warn">
          <div className="tx-warn-head">
            <span className="tx-ln tx-l" />
            <span className="tx-ic">
              <svg viewBox="0 0 24 24">
                <path d="M12 2 4 5v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V5l-8-3z" />
                <path
                  d="M9 12l2 2 4-4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="tx-t">Güvenlik Uyarısı</span>
            <span className="tx-ln tx-r" />
          </div>
          <p className="tx-warn-main">
            <b>Hiçbir şekilde</b> hiçbir hesabınızın bilgisini talep etmiyoruz!
          </p>
          <p className="tx-warn-sub">
            Ne <b className="tx-tk">Telegram</b>/<b className="tx-tk">Darkex</b>{" "}
            şifresi, ne API <span className="tx-no">Withdraw</span> izni — tek
            amacımız <b className="tx-tk">Darkex</b> üzerinde sizin adınıza{" "}
            <b className="tx-tk">hedge</b> işlemleri yürütmek. Fonlarınız her
            zaman kendi hesabınızda kalır.
          </p>
          <div className="tx-warn-pts">
            <div className="tx-pair">
              <span>
                <svg viewBox="0 0 24 24">
                  <path
                    d="M20 6 9 17l-5-5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>{" "}
                Şifre yok
              </span>
              <span>
                <svg viewBox="0 0 24 24">
                  <path
                    d="M20 6 9 17l-5-5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>{" "}
                Withdraw yok
              </span>
            </div>
            <div className="tx-pair">
              <span>
                <svg viewBox="0 0 24 24">
                  <path
                    d="M4 19V5M4 15l4.5-4.5 3.5 3 7-7.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>{" "}
                Analiz Var
              </span>
              <span>
                <svg viewBox="0 0 24 24">
                  <path
                    d="M3 17l6-6 4 4 8-8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 7h6v6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>{" "}
                Kazanç Var
              </span>
            </div>
          </div>
        </div>
      </main>

      <div className="tx-foot">
        Developed by <b>PARANOYA</b>
      </div>
    </div>
  );
}
